/**
 * GLBA Session Service - Client-side
 *
 * Enterprise orgs (Option A): Server-side session authority
 *   - sessionsCreate on login
 *   - sessionsHeartbeat every N minutes
 *   - sessionsEnd on logout
 *
 * Non-enterprise (Option B): Stateless + session events only
 *   - sessionsCreate logs login_success event
 *   - sessionsEnd logs logout event
 *   - sessionsLogEvent for privileged actions
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { subscriptionService } from '@/firebase/subscriptionService';

// Heartbeat interval for Enterprise sessions (minutes)
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

// localStorage keys (used by AuthContext and OrganizationContext)
export const GLBA_SESSION_ID_KEY = 'glbaSessionId';
export const GLBA_ORG_ID_KEY = 'glbaOrgId';

// Module-level heartbeat cleanup so org switch can stop/restart
let heartbeatCleanup: (() => void) | null = null;

export function stopHeartbeat(): void {
  if (heartbeatCleanup) {
    heartbeatCleanup();
    heartbeatCleanup = null;
  }
}

// Simple device fingerprint (hash of navigator props for audit)
function getDeviceFingerprint(): string {
  if (typeof navigator === 'undefined') return '';
  try {
    const str = [
      navigator.userAgent,
      navigator.language,
      navigator.hardwareConcurrency || 0,
      screen.width,
      screen.height,
    ].join('|');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  } catch {
    return '';
  }
}

export interface SessionCreateResult {
  success: boolean;
  mode: 'server_session' | 'stateless';
  sessionId?: string;
  expiresAt?: number;
  heartbeatIntervalMinutes?: number;
  error?: string;
}

export interface SessionEndResult {
  success: boolean;
  error?: string;
}

/**
 * Check if organization is Enterprise (uses Option A: server-side sessions)
 */
export async function isEnterpriseSessionOrg(organizationId: string): Promise<boolean> {
  const result = await subscriptionService.getSubscription(organizationId);
  return result.success && result.data?.plan === 'enterprise';
}

/**
 * Create session on login.
 * Enterprise: Server-side session record + concurrency limits
 * Non-enterprise: Logs login_success event only
 */
export async function createSession(organizationId: string): Promise<SessionCreateResult> {
  try {
    const functions = getFunctions();
    const sessionsCreate = httpsCallable<
      { organizationId: string; deviceFingerprint?: string; userAgent?: string; ipHash?: string },
      { success: boolean; mode: string; sessionId?: string; expiresAt?: number; heartbeatIntervalMinutes?: number }
    >(functions, 'sessionsCreate');

    const result = await sessionsCreate({
      organizationId,
      deviceFingerprint: getDeviceFingerprint(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });

    const data = result.data as any;
    return {
      success: data.success,
      mode: data.mode,
      sessionId: data.sessionId,
      expiresAt: data.expiresAt,
      heartbeatIntervalMinutes: data.heartbeatIntervalMinutes,
    };
  } catch (error: any) {
    console.warn('Session create failed (non-blocking):', error?.message || error);
    return { success: false, mode: 'stateless', error: error?.message };
  }
}

/**
 * Heartbeat for Enterprise sessions. Call every HEARTBEAT_INTERVAL_MINUTES.
 * Non-enterprise: no-op (returns success)
 */
export async function heartbeatSession(
  sessionId: string,
  organizationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const functions = getFunctions();
    const sessionsHeartbeat = httpsCallable<
      { sessionId: string; organizationId: string },
      { success: boolean }
    >(functions, 'sessionsHeartbeat');

    const result = await sessionsHeartbeat({ sessionId, organizationId });
    const data = result.data as any;
    return { success: data?.success ?? true };
  } catch (error: any) {
    console.warn('Session heartbeat failed:', error?.message || error);
    return { success: false, error: error?.message };
  }
}

/**
 * End session on logout.
 * Enterprise: Updates session record
 * Non-enterprise: Logs logout event
 */
export async function endSession(
  sessionId: string | null,
  organizationId: string | null,
  reason: 'logout' | 'timeout' | 'admin_force' = 'logout'
): Promise<SessionEndResult> {
  try {
    const functions = getFunctions();
    const sessionsEnd = httpsCallable<
      { sessionId?: string; organizationId?: string; reason?: string },
      { success: boolean }
    >(functions, 'sessionsEnd');

    const result = await sessionsEnd({
      sessionId: sessionId || undefined,
      organizationId: organizationId || undefined,
      reason,
    });
    const data = result.data as any;
    return { success: data?.success ?? true };
  } catch (error: any) {
    console.warn('Session end failed (non-blocking):', error?.message || error);
    return { success: false, error: error?.message };
  }
}

/**
 * Log privileged action (Option B - non-Enterprise)
 * Use when performing sensitive operations that should be audited
 */
export async function logSessionEvent(
  organizationId: string,
  eventType: 'privileged_action' | 'token_refresh' | 'reauth',
  metadata?: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  try {
    const functions = getFunctions();
    const sessionsLogEvent = httpsCallable<
      { organizationId: string; eventType: string; metadata?: Record<string, unknown> },
      { success: boolean }
    >(functions, 'sessionsLogEvent');

    const result = await sessionsLogEvent({ organizationId, eventType, metadata });
    const data = result.data as any;
    return { success: data?.success ?? true };
  } catch (error: any) {
    console.warn('Session event log failed:', error?.message || error);
    return { success: false, error: error?.message };
  }
}

/**
 * Start heartbeat interval for Enterprise sessions.
 * Returns cleanup function. Also stores cleanup in module for stopHeartbeat().
 */
export function startHeartbeatInterval(
  sessionId: string,
  organizationId: string
): () => void {
  stopHeartbeat();
  const intervalId = setInterval(() => {
    heartbeatSession(sessionId, organizationId);
  }, HEARTBEAT_INTERVAL_MS);

  const cleanup = () => {
    clearInterval(intervalId);
    if (heartbeatCleanup === cleanup) heartbeatCleanup = null;
  };
  heartbeatCleanup = cleanup;
  return cleanup;
}

/**
 * Ensure we have a GLBA session for the current organization.
 * Call when: (1) app loads and currentOrganization is set, (2) user switches org.
 * Ends any previous org's session, then creates session for the given org.
 * Uses GLBA_SESSION_ID_KEY and GLBA_ORG_ID_KEY in localStorage.
 */
export async function ensureSessionForCurrentOrg(organizationId: string): Promise<void> {
  if (typeof localStorage === 'undefined') return;

  const prevOrgId = localStorage.getItem(GLBA_ORG_ID_KEY);
  const prevSessionId = localStorage.getItem(GLBA_SESSION_ID_KEY);

  // Already have an active session for this org (e.g. just logged in with this org)
  if (prevOrgId === organizationId && prevSessionId) {
    return;
  }

  // End previous org's session if switching orgs
  if (prevOrgId && prevOrgId !== organizationId) {
    await endSession(prevSessionId, prevOrgId, 'logout');
    stopHeartbeat();
    localStorage.removeItem(GLBA_SESSION_ID_KEY);
    localStorage.removeItem(GLBA_ORG_ID_KEY);
  }

  const result = await createSession(organizationId);
  if (result.success && result.mode === 'server_session' && result.sessionId) {
    localStorage.setItem(GLBA_SESSION_ID_KEY, result.sessionId);
    localStorage.setItem(GLBA_ORG_ID_KEY, organizationId);
    startHeartbeatInterval(result.sessionId, organizationId);
    console.log('GLBA session: server_session created for org', organizationId);
  } else {
    localStorage.removeItem(GLBA_SESSION_ID_KEY);
    localStorage.removeItem(GLBA_ORG_ID_KEY);
    if (result.success) {
      console.log('GLBA session: login_success event logged for org', organizationId);
    } else {
      console.warn('GLBA session create failed:', result.error);
    }
  }
}

export { HEARTBEAT_INTERVAL_MS };
